import React, { useState, useMemo, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  CircularProgress,
  Box,
  Typography,
  Chip,
  IconButton,
  Popover,
  Checkbox,
  FormControlLabel,
  Stack,
  Divider,
  Toolbar,
  Tooltip,
} from '@mui/material';
import { ViewColumn as ViewColumnIcon } from '@mui/icons-material';
import { SortOrder } from '../types/table';
import dayjs from 'dayjs';

export interface ClientSideTableColumn<T> {
  id: keyof T;
  label: string;
  width?: string;
  firstCell?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
}

export interface ClientSideTableProps<T> {
  data: T[];
  columns: ClientSideTableColumn<T>[];
  loading?: boolean;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  initialPage?: number;
  initialRowsPerPage?: number;
  initialOrder?: SortOrder;
  initialOrderBy?: keyof T;
  rowsPerPageOptions?: number[];
  enableColumnVisibility?: boolean;
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityChange?: (model: Record<string, boolean>) => void;
  toolbarContent?: React.ReactNode;
}

export default function ClientSideTable<T extends { id?: string | number }>({
  data,
  columns,
  loading = false,
  onRowClick,
  emptyMessage = 'No data available',
  initialPage = 0,
  initialRowsPerPage = 10,
  initialOrder = 'asc',
  initialOrderBy,
  rowsPerPageOptions = [10, 25, 50, 100],
  enableColumnVisibility = false,
  columnVisibilityModel: externalColumnVisibilityModel,
  onColumnVisibilityChange,
  toolbarContent,
}: ClientSideTableProps<T>) {
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [order, setOrder] = useState<SortOrder>(initialOrder);
  const [orderBy, setOrderBy] = useState<keyof T | undefined>(initialOrderBy);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >(() => {
    if (
      externalColumnVisibilityModel &&
      Object.keys(externalColumnVisibilityModel).length > 0
    ) {
      return externalColumnVisibilityModel;
    }
    const initialModel: Record<string, boolean> = {};
    columns.forEach((col) => {
      initialModel[String(col.id)] = true;
    });
    return initialModel;
  });
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(
    null
  );

  // Sync internal state with external columnVisibilityModel prop
  useEffect(() => {
    if (
      externalColumnVisibilityModel &&
      Object.keys(externalColumnVisibilityModel).length > 0
    ) {
      setColumnVisibilityModel(externalColumnVisibilityModel);
    }
  }, [externalColumnVisibilityModel]);

  // Reset to first page when data is replaced (e.g. new report results)
  useEffect(() => {
    setPage(0);
  }, [data]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return order === 'asc' ? -1 : 1;
      if (bValue == null) return order === 'asc' ? 1 : -1;

      // Check if this is a date column by looking for date-related field names
      const isDateColumn =
        String(orderBy).includes('date') ||
        String(orderBy).includes('Date') ||
        String(orderBy).includes('_at') ||
        String(orderBy).includes('_time');

      // Handle date strings - convert to dayjs for proper comparison
      if (
        isDateColumn &&
        typeof aValue === 'string' &&
        typeof bValue === 'string'
      ) {
        // Try multiple date formats for Safari compatibility
        let aDate = dayjs(aValue, 'MM-DD-YYYY', true);
        let bDate = dayjs(bValue, 'MM-DD-YYYY', true);

        // If strict parsing failed, try loose parsing
        if (!aDate.isValid()) {
          aDate = dayjs(aValue);
        }
        if (!bDate.isValid()) {
          bDate = dayjs(bValue);
        }

        // If both dates are valid, compare them
        if (aDate.isValid() && bDate.isValid()) {
          return order === 'asc'
            ? aDate.valueOf() - bDate.valueOf()
            : bDate.valueOf() - aDate.valueOf();
        }
        // If dates are invalid, fall back to string comparison
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (aValue instanceof Date && bValue instanceof Date) {
        return order === 'asc'
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      // Fallback to string comparison
      const aStr = String(aValue);
      const bStr = String(bValue);
      return order === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [data, order, orderBy]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const handleSort = (property: keyof T) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleRowClick = (row: T) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnVisibilityToggle = (columnId: string) => {
    const newModel = {
      ...columnVisibilityModel,
      [columnId]: !columnVisibilityModel[columnId],
    };
    setColumnVisibilityModel(newModel);
    onColumnVisibilityChange?.(newModel);
  };

  // Filter visible columns
  const visibleColumns = useMemo(() => {
    if (!enableColumnVisibility) return columns;
    return columns.filter(
      (col) => columnVisibilityModel[String(col.id)] !== false
    );
  }, [columns, columnVisibilityModel, enableColumnVisibility]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {(enableColumnVisibility || toolbarContent) && (
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            minHeight: '56px !important',
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
          }}
        >
          <Box sx={{ flex: '1 1 100%' }}>{toolbarContent}</Box>
          {enableColumnVisibility && (
            <Tooltip title="Show/Hide Columns">
              <IconButton onClick={handleColumnMenuOpen} size="small">
                <ViewColumnIcon />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
      )}
      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={String(column.id)}
                  style={{ width: column.width }}
                  sortDirection={orderBy === column.id ? order : false}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderBottom: '2px solid',
                    borderBottomColor: 'primary.dark',
                    '& .MuiTableSortLabel-root': {
                      color: 'primary.contrastText',
                      '&:hover': {
                        color: 'primary.contrastText',
                      },
                      '&.Mui-active': {
                        color: 'primary.contrastText',
                        '& .MuiTableSortLabel-icon': {
                          color: 'primary.contrastText',
                        },
                      },
                    },
                  }}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => (
                <TableRow
                  key={row.id || index}
                  hover={!!onRowClick}
                  onClick={() => handleRowClick(row)}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    backgroundColor:
                      index % 2 === 0 ? 'background.paper' : 'action.hover',
                    '&:hover': onRowClick
                      ? {
                          backgroundColor: 'action.selected',
                          transition: 'background-color 0.2s ease',
                        }
                      : {},
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={String(column.id)}
                      sx={{
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                        padding: '12px 16px',
                        fontSize: '0.875rem',
                      }}
                    >
                      {column.render ? (
                        column.render(row[column.id], row)
                      ) : (
                        <Box>
                          {column.id === 'local_chapters' &&
                          Array.isArray(row[column.id]) ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                              }}
                            >
                              {(row[column.id] as string[]).map(
                                (chapter: string, idx: number) => (
                                  <Chip
                                    key={idx}
                                    label={chapter}
                                    size="small"
                                    variant="outlined"
                                  />
                                )
                              )}
                            </Box>
                          ) : (
                            String(row[column.id] ?? '')
                          )}
                        </Box>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={data.length}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={rowsPerPageOptions}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }
        labelRowsPerPage="Rows per page:"
        sx={{
          borderTop: '1px solid',
          borderTopColor: 'divider',
          backgroundColor: 'background.default',
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
            {
              fontSize: '0.875rem',
              fontWeight: 500,
            },
        }}
      />

      {/* Column Visibility Menu */}
      <Popover
        open={Boolean(columnMenuAnchor)}
        anchorEl={columnMenuAnchor}
        onClose={handleColumnMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Show/Hide Columns
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={0.5}>
            {columns.map((column) => (
              <FormControlLabel
                key={String(column.id)}
                control={
                  <Checkbox
                    size="small"
                    checked={columnVisibilityModel[String(column.id)] !== false}
                    onChange={() =>
                      handleColumnVisibilityToggle(String(column.id))
                    }
                  />
                }
                label={<Typography variant="body2">{column.label}</Typography>}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </Paper>
  );
}
